import React, { createContext } from 'react';
import { SplitFactory } from '@splitsoftware/splitio-react';
import { useAuth } from '@cnx-saas/react-auth';

interface Config extends Omit<SplitIO.IBrowserSettings, 'core'> {
  core: {
    /**
     * @property authorizationKey - Your API key. More information: @see {@link https://help.split.io/hc/en-us/articles/360019916211-API-keys}.
     *
     */
    authorizationKey?: string;

    /**
     * @property key - Customer identifier. Whatever this means to you. @see {@link https://help.split.io/hc/en-us/articles/360019916311-Traffic-type}.
     *
     */
    key: SplitIO.SplitKey;

    /**
     * @property trafficType - Traffic type associated with the customer identifier. @see {@link https://help.split.io/hc/en-us/articles/360019916311-Traffic-type}.
     * If no provided as a setting it will be required on the client.track() calls.
     */
    trafficType?: string;

    /**
     * @property labelsEnabled - Disable labels from being sent to Split backend. Labels may contain sensitive information.
     * @default true
     */
    labelsEnabled?: boolean;
  };
}

export interface FeatureToggleProviderProps {
  attributes?: SplitIO.Attributes;
  config: Config;
  children: JSX.Element;
}

const defaultConfig = {
  core: { authorizationKey: process.env.REACT_APP_SPLITIO_API_KEY },
};

const FeatureToggleContext = createContext({});

function FeatureToggleProvider({
  config,
  attributes = {},
  children,
}: FeatureToggleProviderProps): JSX.Element {
  const extendedConfig = {
    ...defaultConfig,
    ...config,
    core: {
      ...defaultConfig.core,
      ...config.core,
    },
  } as SplitIO.IBrowserSettings;

  return (
    <FeatureToggleContext.Provider value={attributes}>
      <SplitFactory config={extendedConfig} updateOnSdkUpdate>
        {children}
      </SplitFactory>
    </FeatureToggleContext.Provider>
  );
}

interface FeatureToggleProviderAutomaticProps {
  children: JSX.Element;
}

function FeatureToggleProviderAutomatic({
  children,
}: FeatureToggleProviderAutomaticProps): JSX.Element {
  const auth = useAuth();
  let attributes = {};
  const config = { key: 'anonymous' };

  if (auth.initialized && auth.authenticated) {
    attributes = {
      companyId: auth.user.tenantId,
      email: auth.user.email,
      userId: auth.user.userId,
    };

    config.key = auth.user.email;
  }

  const extendedConfig = {
    ...defaultConfig,
    streamingEnabled: false,
    scheduler: {
      featuresRefreshRate: 1800, // default 60 sec
      segmentsRefreshRate: 1800, // default 60 sec
      impressionsRefreshRate: 300, // default 300 sec
      impressionsQueueSize: 30000, // default 30000 items
      eventsPushRate: 1800, // default 60 sec
      eventsQueueSize: 500, // default 500 items
      telemetryRefreshRate: 3600, // default 3600 sec
    },
    core: {
      ...defaultConfig.core,
      ...config,
    },
  } as SplitIO.IBrowserSettings;

  return (
    <FeatureToggleContext.Provider value={attributes}>
      <SplitFactory config={extendedConfig}>{children}</SplitFactory>
    </FeatureToggleContext.Provider>
  );
}

export {
  FeatureToggleProvider,
  FeatureToggleContext,
  FeatureToggleProviderAutomatic,
};
